import { ISelectOption } from '@constants/entities/ui';
import { IDomain } from '@store/services/projects/types';
import { findWideDomain } from '@utils/domains';
import { ITreeItem } from '@views/Project/helpers';

type CheckIsAllDomainsSelectedParams = {
  selectedDomains: ISelectOption[];
  projectDomains: IDomain[];
};

const findNode = (
  node: ITreeItem | undefined,
  nodeId: string,
): ITreeItem | null => {
  if (!node) return null;
  if (node.id === nodeId) return node;
  for (const child of node.children ?? []) {
    const found = findNode(child, nodeId);
    if (found) return found;
  }
  return null;
};

const collectOptions = (node: ITreeItem | undefined): ISelectOption[] => {
  if (!node) return [];
  return [
    { value: node.id, label: node.name },
    ...(node.children ?? []).flatMap((child) => collectOptions(child)),
  ];
};
export const getNodeWithDescendantsOptions = (
  root: ITreeItem | undefined,
  nodeId: string,
): ISelectOption[] => {
  const startNode = findNode(root, nodeId);
  return collectOptions(startNode ?? undefined);
};
export const getNodeDescendantIds = (
  root: ITreeItem | undefined,
  nodeId: string,
): string[] => {
  const options = getNodeWithDescendantsOptions(root, nodeId);
  return options.map((o) => o.value as string);
};

export const checkIsAllDomainsSelected = ({
  selectedDomains,
  projectDomains,
}: CheckIsAllDomainsSelectedParams) => {
  const wideDomain = findWideDomain(projectDomains);

  const isAllDomainsSelected = projectDomains.every(
    ({ id }) =>
      wideDomain?.id === id ||
      selectedDomains.some(({ value }) => value === id),
  );

  const isWideSelected = selectedDomains.some(
    ({ value }) => value === wideDomain?.id,
  );

  return { isAllDomainsSelected, isWideSelected };
};

/**
 * Given a list of selected domains, auto-select any parent domain
 * whose children are ALL already in the selection.
 * Also auto-removes parents whose children are NOT all selected.
 *
 * Uses parentId relationships (not level) so it works for any depth.
 */
export const reconcileParentSelections = (
  selectedDomains: ISelectOption[],
  projectDomains: IDomain[],
): ISelectOption[] => {
  const wideDomain = findWideDomain(projectDomains);

  // Group domains by their parentId, excluding direct children of the wide domain
  // (enterprise auto-selection is handled separately)
  const childrenByParent = new Map<string, IDomain[]>();

  for (const domain of projectDomains) {
    if (domain.parentId && domain.parentId !== wideDomain?.id) {
      const existing = childrenByParent.get(domain.parentId) ?? [];
      existing.push(domain);
      childrenByParent.set(domain.parentId, existing);
    }
  }

  const toAdd: ISelectOption[] = [];

  for (const [parentId, children] of childrenByParent) {
    const allChildrenSelected = children.every((child) =>
      selectedDomains.some((d) => d.value === child.id),
    );
    const parentInValue = selectedDomains.some((d) => d.value === parentId);

    if (allChildrenSelected && !parentInValue) {
      const parent = projectDomains.find((d) => d.id === parentId);

      if (parent) {
        toAdd.push({ value: parent.id, label: parent.name });
      }
    }
  }

  return [...selectedDomains, ...toAdd];
};

export type GetSelectedDomainsParams = {
  selectedDomains: ISelectOption[] | undefined;
  wideDomain: ITreeItem | undefined;
};

export const getSelectedDomains = ({
  selectedDomains,
  wideDomain,
}: GetSelectedDomainsParams) => {
  const isWideSelected = selectedDomains?.some(
    ({ value }) => value === wideDomain?.id,
  );

  if (isWideSelected) {
    return (
      wideDomain?.children
        ?.map(({ id, children }) => [
          id,
          ...(children?.map(({ id: childId }) => childId) ?? []),
        ])
        ?.flat(2) ?? []
    );
  }

  const currentValues =
    (selectedDomains?.map(({ value }) => value) as string[]) ?? [];

  return currentValues;
};
